import React from "react";

import cx from "classnames";
import omit from "lodash.omit";

import { Icon, IconDictionary } from "../Icon";
import { UserDisplay } from "../UserDisplay";

import { User } from "./UserSelect";
import styles from "./UserSelect.scss";

export interface UserSelectInputProps {
  /**
   * Reference to the input element
   */
  inputRef?: (ref: HTMLInputElement | null) => void;
  /**
   * Reference to the container element
   */
  innerRef?: (ref: HTMLDivElement | null) => void;
  /**
   * Icon to be used for the input
   */
  icon?: IconDictionary;
  /**
   * Props to be passed to the input element
   */
  inputProps?: Record<string, any> &
    React.InputHTMLAttributes<HTMLInputElement> & { error?: string };
  /**
   * Whether multiple users can be selected
   */
  multipleUsers?: boolean;
  /**
   * Callback when a user is cleared
   */
  onUserCleared?: (user: User) => void;
  /**
   * Array of selected users
   */
  selectedUsers?: User[];
  /**
   * Used to disable without complete disabled styling
   */
  maxUsers?: boolean;
  /**
   * Whether to show the user icon
   */
  showIcon?: boolean;
}

/**
 * Input component for UserSelect that handles the display of selected users
 * and the input field for searching users.
 */
export function UserSelectInput({
  showIcon,
  icon = "search",
  innerRef,
  inputProps = {},
  inputRef,
  multipleUsers,
  onUserCleared,
  selectedUsers = [],
  maxUsers,
}: UserSelectInputProps) {
  const { disabled, error } = inputProps;

  const userSelectContainerClassNames = cx(styles["user-select-container"], {
    [styles["user-select-container--disabled"]]: disabled,
    [styles["user-select-container--error"]]: error,
    [styles["user-select-container--multi"]]: multipleUsers,
  });
  const userDisplayContainerClassNames = multipleUsers
    ? styles["multiple-user-display-container"]
    : styles["single-user-display-container"];

  const baseDataTestId = inputProps["data-testid"] || "user-select";

  const shouldShowInput = multipleUsers || !selectedUsers[0];

  return (
    <div
      className={userSelectContainerClassNames}
      ref={innerRef}
      data-testid={baseDataTestId + "-container"}
    >
      {selectedUsers.map(
        (user) =>
          user && (
            <div key={user.id} className={userDisplayContainerClassNames}>
              <UserDisplay
                pill={true}
                userProfile={{
                  ...user,
                  jobTitle:
                    typeof user.jobTitle === "string"
                      ? user.jobTitle
                      : user.jobTitle?.name,
                  primaryGroup:
                    typeof user.primaryGroup === "string"
                      ? user.primaryGroup
                      : user.primaryGroup?.name,
                }}
                onClose={(user) =>
                  onUserCleared?.({
                    firstName: String(user.firstName),
                    id: String(user.id),
                    image30: user.image30,
                    image50: user.image50,
                    image: user.image,
                    jobTitle: user.jobTitle,
                    initials: String(user.initials),
                    lastName: String(user.lastName),
                    name: String(user.name),
                  })
                }
              />
            </div>
          )
      )}
      {shouldShowInput && (
        <div
          className={styles["user-input-container"]}
          data-testid={baseDataTestId + "-input-container"}
        >
          <input
            data-testid={baseDataTestId + "-input"}
            {...omit(
              inputProps,
              "onTextChange",
              "errorId",
              "helperTextId",
              "highlightedIndex"
            )}
            className={styles["user-input"]}
            disabled={disabled || maxUsers}
            ref={inputRef}
            data-lpignore="true"
          />
          <div
            className={styles["user-input-focus-border"]}
            data-testid={baseDataTestId + "-input-focus-border"}
          />
          {showIcon && (
            <Icon
              icon={icon}
              size={16}
              color="#666666"
              decorative
              data-testid={baseDataTestId + "-icon"}
            />
          )}
        </div>
      )}
    </div>
  );
}
